import React, { useState } from 'react';
import { User, Mail, Lock, Sparkles } from 'lucide-react';
import chatApi from '../../../services/chatApi';
import { useAuth } from '../../../context/AuthContext';

/**
 * Multi-step signup card — one step per card instance.
 * Steps: name → email → password → goal
 * Each step saves into in-memory draft on window (`__formaSignup__`) and
 * advances by emitting a synthetic user message with the next step card.
 * Final step (goal) triggers actual signup + auth.
 */

const goalOptions = [
  { id: 'feel_good', label: '🌿 Feel better in my body' },
  { id: 'pain_relief', label: '🩹 Sort out an ache or pain' },
  { id: 'energy', label: '⚡ Get my energy back' },
  { id: 'mobility', label: '🤸 Move easier, less stiff' },
  { id: 'stress_sleep', label: '🌙 Less stress, better sleep' },
  { id: 'rehab', label: '🔁 Come back from an injury' },
  { id: 'tone', label: '💪 Tone up gently' },
];

const SignupStepCard = ({ data, onAction }) => {
  const step = data?.step || 'name';
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const auth = useAuth();

  const draft = (window.__formaSignup__ = window.__formaSignup__ || {});

  const next = (nextStep, extraUserMsg) => {
    onAction?.({
      type: 'local_advance',
      payload: {
        user_text: extraUserMsg,
        next_card: { type: 'signup_step', data: { step: nextStep } },
      },
    });
  };

  const submitFinal = async (goal) => {
    setLoading(true); setError(null);
    try {
      const res = await chatApi.signupChat({
        name: draft.name, email: draft.email, password: draft.password, goal,
      });
      const { token, user, forma_messages } = res.data;
      localStorage.setItem('hpme_token', token);
      auth?.loginFromResponse?.(token, user);
      window.__formaSignup__ = {}; // clear draft
      onAction?.({ type: 'auth_success', payload: { user, messages: forma_messages || [], user_text: goalOptions.find(o=>o.id===goal)?.label } });
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'name') {
    return (
      <form className="flex items-center gap-2 rounded-sm border border-zinc-700 bg-zinc-900/60 p-3" onSubmit={e => { e.preventDefault(); if (!value.trim()) return; draft.name = value.trim(); next('email', value.trim()); }} data-testid="card-signup-name">
        <User className="w-4 h-4 text-zinc-500" />
        <input autoFocus className="bg-transparent outline-none flex-1 text-sm text-white" placeholder="Your name"
          value={value} onChange={e => setValue(e.target.value)} data-testid="signup-name-input" />
        <button type="submit" className="text-xs font-secondary font-bold bg-primary text-black px-3 py-1 rounded-sm" data-testid="signup-name-submit">Continue</button>
      </form>
    );
  }
  if (step === 'email') {
    return (
      <form className="flex items-center gap-2 rounded-sm border border-zinc-700 bg-zinc-900/60 p-3" onSubmit={e => { e.preventDefault(); if (!value.trim()) return; draft.email = value.trim().toLowerCase(); next('password', value.trim().toLowerCase()); }} data-testid="card-signup-email">
        <Mail className="w-4 h-4 text-zinc-500" />
        <input autoFocus type="email" className="bg-transparent outline-none flex-1 text-sm text-white" placeholder="Email"
          value={value} onChange={e => setValue(e.target.value)} required data-testid="signup-email-input" />
        <button type="submit" className="text-xs font-secondary font-bold bg-primary text-black px-3 py-1 rounded-sm" data-testid="signup-email-submit">Continue</button>
      </form>
    );
  }
  if (step === 'password') {
    return (
      <form className="flex items-center gap-2 rounded-sm border border-zinc-700 bg-zinc-900/60 p-3" onSubmit={e => { e.preventDefault(); if (value.length < 8) { setError('At least 8 characters'); return; } draft.password = value; next('goal', '••••••••'); }} data-testid="card-signup-password">
        <Lock className="w-4 h-4 text-zinc-500" />
        <input autoFocus type="password" className="bg-transparent outline-none flex-1 text-sm text-white" placeholder="Create a password (8+ chars)"
          value={value} onChange={e => setValue(e.target.value)} required data-testid="signup-password-input" />
        <button type="submit" className="text-xs font-secondary font-bold bg-primary text-black px-3 py-1 rounded-sm" data-testid="signup-password-submit">Continue</button>
        {error && <p className="text-red-400 text-xs ml-2">{error}</p>}
      </form>
    );
  }
  if (step === 'goal') {
    return (
      <div className="space-y-3 rounded-sm border border-zinc-700 bg-zinc-900/60 p-4" data-testid="card-signup-goal">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <p className="text-sm text-zinc-300">How are you feeling — anything you'd like to work on?</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {goalOptions.map(o => (
            <button key={o.id} type="button" disabled={loading}
              onClick={() => submitFinal(o.id)}
              className="px-3 py-2 rounded-sm border border-zinc-700 bg-zinc-950 hover:border-primary hover:bg-primary/10 hover:text-primary text-sm font-secondary font-bold disabled:opacity-50 transition-all"
              data-testid={`signup-goal-${o.id}`}>
              {o.label}
            </button>
          ))}
        </div>
        {error && <p className="text-red-400 text-xs" data-testid="signup-error">{error}</p>}
        {loading && <p className="text-zinc-500 text-xs">Creating your account…</p>}
      </div>
    );
  }
  return null;
};

export default SignupStepCard;
